import React from "react";
import Icon from "../components/Icon";

const money = (n) => (n == null ? "—" : "$" + Math.round(Number(n)).toLocaleString());
const fmtDate = (d) => { const t = new Date(d); return isNaN(t) ? "—" : t.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); };

// Presentational. State (snapshots / pulling) lives in ProjectDetail so the GC
// burn-down can share the same Procore numbers.
export default function ProcoreBudget({ snaps = [], loading, pulling, err, onPull, procoreId, isAdmin }) {
  const latest = snaps[snaps.length - 1] || null;
  const lines = latest ? [...latest.lines].sort((a, b) => String(a.BudgetCode).localeCompare(String(b.BudgetCode))) : [];

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div className="card-header">
        <div className="card-title">Labor Budget <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)" }}>· Procore "Hardaway View REV 1"</span></div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={onPull} disabled={pulling || !procoreId} style={{ padding: "6px 14px", fontSize: 13 }}>
            {pulling ? <span className="flex items-center gap-8"><span className="spinner" style={{ width: 14, height: 14 }} /> Pulling…</span>
              : <span className="flex items-center gap-8"><Icon name="download" size={14} /> Pull Budget from Procore</span>}
          </button>
        )}
      </div>

      {!procoreId && (
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          No Procore Project ID set for this job. Add it in Admin Console → the project's <b>Procore Project ID</b> field to enable live budget pulls.
        </div>
      )}
      {err && <div style={{ color: "var(--red)", fontSize: 13, marginBottom: 8 }}>{err}</div>}

      {loading ? (
        <div className="empty"><div className="spinner" /></div>
      ) : !latest ? (
        procoreId && <div className="empty"><div className="empty-icon"><Icon name="chart" /></div><div className="empty-text">No budget pulled yet. Click "Pull Budget from Procore" to capture the current labor budget.</div></div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
            Last pulled {fmtDate(latest.date)}{latest.pulledBy ? ` by ${latest.pulledBy}` : ""} · {snaps.length} snapshot{snaps.length !== 1 ? "s" : ""} on record
          </div>

          {/* Totals */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 16 }}>
            <Tot label="Original Budget" v={latest.totals.original} />
            <Tot label="Revised Budget" v={latest.totals.revised} />
            <Tot label="Job-to-Date Cost" v={latest.totals.jtd} />
            <Tot label="Forecast Cost to Complete" v={latest.totals.forecast} />
            {(() => {
              const fac = latest.totals.jtd + latest.totals.forecast;
              const variance = latest.totals.revised - fac;
              return <Tot label="Variance vs Revised" v={variance} color={variance < 0 ? "var(--red)" : "var(--green)"} signed />;
            })()}
          </div>

          {/* Trend across snapshots */}
          {snaps.length > 1 && <Trend snaps={snaps} />}

          {/* Line table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "right", color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  <th style={{ textAlign: "left", padding: "6px 8px" }}>Cost Code</th>
                  <th style={{ textAlign: "left", padding: "6px 8px" }}>Resource</th>
                  <th style={{ padding: "6px 8px" }}>Original</th>
                  <th style={{ padding: "6px 8px" }}>Revised</th>
                  <th style={{ padding: "6px 8px" }}>JTD</th>
                  <th style={{ padding: "6px 8px" }}>Fcst to Complete</th>
                  <th style={{ padding: "6px 8px" }}>Variance</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const fac = (Number(l.JobToDateCosts) || 0) + (Number(l.ForecastCostToComplete) || 0);
                  const v = (Number(l.RevisedBudget) || 0) - fac;
                  return (
                    <tr key={i} style={{ borderTop: "1px solid var(--border)" }}>
                      <td style={{ padding: "7px 8px", fontFamily: "monospace", fontSize: 12 }}>{l.BudgetCode}</td>
                      <td style={{ padding: "7px 8px" }}>{l.Description}</td>
                      <td style={{ padding: "7px 8px", textAlign: "right", color: "var(--text-muted)" }}>{money(l.OriginalBudget)}</td>
                      <td style={{ padding: "7px 8px", textAlign: "right" }}>{money(l.RevisedBudget)}</td>
                      <td style={{ padding: "7px 8px", textAlign: "right", color: "var(--text-muted)" }}>{money(l.JobToDateCosts)}</td>
                      <td style={{ padding: "7px 8px", textAlign: "right" }}>{money(l.ForecastCostToComplete)}</td>
                      <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 700, color: v < 0 ? "var(--red)" : "var(--green)" }}>{v < 0 ? "-" : ""}{money(Math.abs(v))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>
            Variance = Revised Budget − (Job-to-Date + Forecast Cost to Complete). Negative (red) = projected overrun. Pulled live from Procore; each pull is saved as a dated snapshot.
          </div>
        </>
      )}
    </div>
  );
}

function Tot({ label, v, color, signed }) {
  return (
    <div className="card" style={{ padding: 14, background: "var(--bg-elevated)" }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || "var(--text-primary)" }}>
        {signed && v >= 0 ? "+" : signed && v < 0 ? "-" : ""}{"$" + Math.round(Math.abs(Number(v) || 0)).toLocaleString()}
      </div>
      <div style={{ fontSize: 11, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 }}>{label}</div>
    </div>
  );
}

// Revised vs Forecast-at-completion across snapshots — a small inline line chart.
function Trend({ snaps }) {
  const W = 640, H = 120, padL = 8, padR = 8, padT = 10, padB = 22;
  const pts = snaps.map(s => ({ date: s.date, revised: s.totals.revised, fac: s.totals.jtd + s.totals.forecast }));
  const max = Math.max(1, ...pts.map(p => Math.max(p.revised, p.fac)));
  const n = pts.length;
  const x = (i) => padL + (n === 1 ? (W - padL - padR) / 2 : (i / (n - 1)) * (W - padL - padR));
  const y = (v) => padT + (1 - v / max) * (H - padT - padB);
  const path = (key) => pts.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(" ");
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 16, fontSize: 11, color: "var(--text-secondary)", marginBottom: 4 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, height: 3, background: "#3b6fb5", display: "inline-block" }} /> Revised Budget</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, height: 3, background: "#d98f00", display: "inline-block" }} /> Forecast at Completion (JTD + Cost to Complete)</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H, background: "var(--bg-elevated)", borderRadius: 8 }}>
        <path d={path("revised")} fill="none" stroke="#3b6fb5" strokeWidth="2" />
        <path d={path("fac")} fill="none" stroke="#d98f00" strokeWidth="2" />
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(p.revised)} r="3" fill="#3b6fb5" />
            <circle cx={x(i)} cy={y(p.fac)} r="3" fill="#d98f00" />
            <text x={x(i)} y={H - 6} textAnchor="middle" fontSize="9" fill="var(--text-muted)">
              {new Date(p.date).toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
